import React, { useRef, useState } from "react";
import { Download, Maximize2, ChevronDown, ChevronUp, Box } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mb = (b) => `${(b / (1024 * 1024)).toFixed(b < 10485760 ? 2 : 1)} MB`;
const secs = (s) => { if (s == null) return "—"; const t = Math.round(s); return t < 90 ? `${t}s` : `${Math.floor(t / 60)}m ${t % 60}s`; };

function Details({ job }) {
  const m = job.metrics || {}, r = job.result || {};
  const rows = [
    ["Engine", `${m.engine} · Blender ${m.blender_version}`], ["Resolution", m.resolution], ["Frame rate", `${r.fps} FPS`],
    ["Frames", r.frames?.toLocaleString()], ["Length", `${r.duration}s`], ["Render time", secs(m.render_seconds)],
    ["Per frame", m.seconds_per_frame != null ? `${m.seconds_per_frame}s` : "—"], ["Total time", secs(m.total_seconds)],
    ["Device", m.device], ["CPU time", secs(m.cpu_seconds)], ["Peak memory", m.peak_memory_mb ? `${m.peak_memory_mb} MB` : "—"],
    ["File size", r.size_bytes ? mb(r.size_bytes) : "—"], ["Cost", m.cost_usd != null ? `$${m.cost_usd}` : "Not calculated yet"],
    ["Credits charged", String(job.charged || 0)],
    ...(m.samples != null ? [["Samples", String(m.samples)]] : []),
    ...(m.features?.length ? [["Effects", m.features.join(" · ")]] : []),
    ...(job.asset ? [["Model", job.asset.name], ["Licence", job.asset.licence], ["Chosen by", job.plan_source === "ai" ? "Anna (AI)" : "keyword match"]] : []),
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]" data-testid="render3d-details">
      {rows.map(([k, v]) => (
        <React.Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="text-slate-200 font-mono break-words" title={String(v)}>{v}</dd></React.Fragment>
      ))}
    </dl>
  );
}

export function VideoPanel({ job }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const r = job.result;
  const full = () => { const v = ref.current; (v.requestFullscreen || v.webkitEnterFullscreen || v.webkitRequestFullscreen)?.call(v); };
  return (
    <div className="space-y-3" data-testid="render3d-done">
      <video ref={ref} src={`${BACKEND}${r.video_url}`} poster={r.thumb_url ? `${BACKEND}${r.thumb_url}` : undefined} controls playsInline preload="metadata"
        className="w-full rounded-xl border border-white/10 bg-black aspect-video" data-testid="render3d-video" />
      <div className="flex flex-wrap gap-2">
        <a href={`${BACKEND}${r.video_url}?download=true`} download data-testid="render3d-download"
          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white text-black text-xs font-medium hover:bg-slate-200 transition-colors">
          <Download className="w-3.5 h-3.5" />Download MP4
        </a>
        <button type="button" onClick={full} data-testid="render3d-fullscreen"
          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-white/15 text-xs text-slate-200 hover:bg-white/10 transition-colors">
          <Maximize2 className="w-3.5 h-3.5" />Full screen
        </button>
        {r.blend_url && (
          <a href={`${BACKEND}${r.blend_url}`} download data-testid="render3d-blend"
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-white/15 text-xs text-slate-200 hover:bg-white/10 transition-colors">
            <Box className="w-3.5 h-3.5" />Blender file
          </a>
        )}
        <button type="button" onClick={() => setOpen(!open)} data-testid="render3d-details-toggle"
          className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-400 hover:text-white transition-colors">
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}Render details
        </button>
      </div>
      {open && <Details job={job} />}
    </div>
  );
}
